import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { MessageSquare, Trash2, Reply, Search, XCircle, CheckCircle } from "lucide-react";

const ITEMS_PER_PAGE = 10;

export default function Comments() {
  const [comments, setComments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPlace, setFilterPlace] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [deletingComment, setDeletingComment] = useState(null);
  const [replyingComment, setReplyingComment] = useState(null);
  const [replyText, setReplyText] = useState("");

  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    axios.get(`${API_URL}/admin/comments`)
      .then(res => {
        setComments(res.data);
      })
      .catch(err => {
        console.error("Yorum verisi çekilemedi:", err);
        setMessage({ type: 'error', text: 'Yorumlar yüklenirken bir hata oluştu.' });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const filteredData = useMemo(() => {
    if (!comments) return []; // comments'in null/undefined olmaması için kontrol
    return comments.filter((item) => {
      const matchPlace = filterPlace ? item.place_name?.toLowerCase() === filterPlace.toLowerCase() : true;
      const matchSearch =
        (item.user_email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.place_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.comment_text || "").toLowerCase().includes(searchTerm.toLowerCase());
      return matchPlace && matchSearch;
    });
  }, [filterPlace, searchTerm, comments]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const uniquePlaces = Array.from(new Set(comments.map((d) => d.place_name))).filter(Boolean);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  const confirmDelete = (comment) => setDeletingComment(comment);

  const handleDelete = async () => {
    try {
      // API endpoint'iniz bu şekildeyse
      await axios.delete(`${API_URL}/admin/comments/${deletingComment.id}`);
      setComments(prev => prev.filter(c => c.id !== deletingComment.id));
      setDeletingComment(null);
      setMessage({ type: 'success', text: 'Yorum başarıyla silindi!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      console.error("Silme hatası:", err);
      setMessage({ type: 'error', text: 'Yorum silinirken bir hata oluştu.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const cancelDelete = () => setDeletingComment(null);

  const openReplyModal = (comment) => {
    setReplyingComment(comment);
    setReplyText("");
  };

  const saveReply = async () => {
    if (!replyText.trim()) {
      setMessage({ type: 'error', text: 'Lütfen bir cevap metni girin.' });
      return;
    }
    try {
      // Cevap gönderme endpoint'iniz varsayımsal olarak bu şekilde
      const res = await axios.post(`${API_URL}/admin/comments/${replyingComment.id}/reply`, {
        reply_text: replyText // Backend'in beklediği anahtar adını kontrol edin
      });
      // Backend cevabı güncellenmiş yorumu döndürüyorsa
      setComments(prev =>
        prev.map(c =>
          c.id === replyingComment.id
            ? { ...c, replies: [...(c.replies || []), res.data] } // replies alanı yoksa boş array ile başlat
            : c
        )
      );
      setReplyingComment(null);
      setMessage({ type: 'success', text: 'Cevap başarıyla gönderildi!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      console.error("Cevap gönderme hatası:", err);
      setMessage({ type: 'error', text: 'Cevap gönderilirken bir hata oluştu.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const cancelReply = () => setReplyingComment(null);

  if (loading) {
    return (
      <div className="flex-1 p-6 bg-gray-900 text-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-xl font-bold">Yorumlar yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 bg-gray-900 text-gray-50 min-h-screen font-sans">
      <div className="flex items-center gap-4 mb-8">
        <MessageSquare className="h-12 w-12 text-blue-400" />
        <h1 className="text-4xl font-extrabold text-white">Yorumlar</h1>
      </div>

      {message.type && (
        <div className={`flex items-center gap-3 p-4 mb-6 rounded-xl border ${
          message.type === 'success' ? 'bg-green-900/30 text-green-400 border-green-700' :
          message.type === 'error' ? 'bg-red-900/30 text-red-400 border-red-700' :
          'bg-blue-900/30 text-blue-400 border-blue-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="h-6 w-6 flex-shrink-0" /> : <XCircle className="h-6 w-6 flex-shrink-0" />}
          <span className="font-medium">{message.text}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0 mb-8 p-4 bg-gray-800 rounded-2xl border border-gray-700">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Kullanıcı, mekan veya yorum ara..."
            className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600 transition-colors"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <select
          className="w-full sm:w-64 px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors cursor-pointer"
          value={filterPlace}
          onChange={(e) => {
            setFilterPlace(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">Tüm Mekanlar</option>
          {uniquePlaces.map((place) => (
            <option key={place} value={place}>{place}</option>
          ))}
        </select>
      </div>

      <div className="bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-700">
        <table className="min-w-full text-gray-300">
          <thead className="bg-gray-700 border-b border-gray-600">
            <tr className="text-left text-sm font-semibold text-gray-400">
              <th className="px-6 py-3">ID</th>
              <th className="px-6 py-3">Kullanıcı E-posta</th>
              <th className="px-6 py-3">Mekan Adı</th>
              <th className="px-6 py-3">Yorum</th>
              <th className="px-6 py-3">Tarih</th>
              <th className="px-6 py-3">Cevaplar</th>
              <th className="px-6 py-3">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-6 text-gray-500">Kayıt bulunamadı.</td>
              </tr>
            ) : (
              paginatedData.map((comment) => (
                <tr key={comment.id} className="hover:bg-gray-700 transition-colors duration-150">
                  <td className="px-6 py-4">{comment.id}</td>
                  <td className="px-6 py-4">{comment.user_email}</td>
                  <td className="px-6 py-4 font-semibold text-white">{comment.place_name}</td>
                  <td className="px-6 py-4 max-w-xs">{comment.comment_text}</td>
                  <td className="px-6 py-4 text-sm">{new Date(comment.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4">
                    {/* Cevaplar alanı için varsayılan bir veri yoksa boş bırak */}
                    {comment.replies && comment.replies.length > 0 ? (
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-400">
                        {comment.replies.map((r, index) => (
                          <li key={index}>{r.text}</li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-gray-500 italic text-sm">Yok</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => openReplyModal(comment)}
                        className="inline-flex items-center gap-1 bg-green-600/20 text-green-400 px-3 py-1 rounded-lg hover:bg-green-600/30 transition-colors duration-200 text-sm"
                      >
                        <Reply className="h-4 w-4" /> Cevapla
                      </button>
                      <button
                        onClick={() => confirmDelete(comment)}
                        className="inline-flex items-center gap-1 bg-red-600/20 text-red-400 px-3 py-1 rounded-lg hover:bg-red-600/30 transition-colors duration-200 text-sm"
                      >
                        <Trash2 className="h-4 w-4" /> Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-3 mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              currentPage === 1 ? "text-gray-600 bg-gray-800 cursor-not-allowed" : "text-white bg-gray-700 hover:bg-gray-600"
            }`}
          >
            Önceki
          </button>
          <span className="px-4 py-2 rounded-lg bg-gray-700 text-white font-semibold">{currentPage} / {totalPages}</span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              currentPage === totalPages ? "text-gray-600 bg-gray-800 cursor-not-allowed" : "text-white bg-gray-700 hover:bg-gray-600"
            }`}
          >
            Sonraki
          </button>
        </div>
      )}

      {/* --- Silme Onayı Modalı --- */}
      {deletingComment && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm z-50 p-4"
          onClick={cancelDelete}
        >
          <div
            className="bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-sm border border-gray-700 text-gray-50 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4">Silme Onayı</h3>
            <p className="text-gray-300 mb-6">
              **{deletingComment.user_email}** kullanıcısının **{deletingComment.place_name}** mekanına ait yorumunu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={cancelDelete}
                className="px-6 py-3 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors font-semibold"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Yorum Cevaplama Modalı --- */}
      {replyingComment && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm z-50 p-4"
          onClick={cancelReply}
        >
          <div
            className="bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-lg border border-gray-700 text-gray-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold">Yorum Cevapla</h3>
              <button
                type="button"
                onClick={cancelReply}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6 p-4 bg-gray-700 rounded-lg border border-gray-600">
              <p className="text-sm font-semibold text-gray-400 mb-1">{replyingComment.user_email}</p>
              <p className="text-gray-200">{replyingComment.comment_text}</p>
            </div>

            <textarea
              rows={5}
              className="w-full px-4 py-3 bg-gray-700 text-gray-200 rounded-lg shadow-inner border border-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
              placeholder="Cevap metnini yazınız..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
            />
            
            <div className="flex justify-end gap-4 mt-8">
              <button
                type="button"
                onClick={cancelReply}
                className="px-6 py-3 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors font-semibold"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={saveReply}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                Gönder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}